<script>
  import { onMount } from 'svelte';
  import { supabase } from '../lib/supabase.js';
  import { workspace, loadWorkspace } from '../lib/stores/workspace.js';
  import CreatePanel from './CreatePanel.svelte';
  import MapTab from './MapTab.svelte';
  import PricingView from './PricingView.svelte';
  import Toast from './Toast.svelte';
  import VentureSwitcher from './VentureSwitcher.svelte';
  import CreditsPill from './CreditsPill.svelte';
  import ElvieButton from './ElvieButton.svelte';
  import ElviePanel from './ElviePanel.svelte';
  import { ui } from '../lib/stores/ui.svelte.js';

  let activeTab = $state('home');
  // The map is the builder's RESULT and lives outside its tabs (Miles 2026-11-05): ?map=1 opens it full screen, and
  // HQ links here with &company=<id> to open that company's design. No builder tabs in that view.
  const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const mapOnly = qs.has('map');
  const mapCompany = qs.get('company');
  // Shown inside HQ (Wovyn frames /builder/?map=1&embed=1): no builder header or counters — HQ is the frame.
  const embed = qs.has('embed');
  let companyName = $state('');
  let libraryFilter = $state('all');
  let user = $state(null);

  onMount(async () => {
    const { data: { user: u } } = await supabase.auth.getUser();
    user = u;
    if (!u) return;
    if (mapOnly && mapCompany) {
      const { data: rows } = await supabase.rpc('fn_bb_my_workspaces');
      const w = (rows || []).find((r) => r.partner_id === mapCompany && r.has_workspace);
      const { data: cos } = await supabase.rpc('fn_bb_my_companies');
      companyName = (cos || []).find((c) => c.partner_id === mapCompany)?.name || '';
      await loadWorkspace(w?.workspace_id);
    } else await loadWorkspace();
  });

  $effect(() => {
    const goCreate = () => { activeTab = 'home'; };
    const goLibrary = (e) => {
      libraryFilter = e?.detail?.kind || 'all';
      activeTab = 'home';
    };
    const goCreateAud = () => { activeTab = 'home'; setTimeout(() => window.dispatchEvent(new CustomEvent('set-create-mode', { detail: 'audience' })), 50); };
    const goCreateDeal = () => { activeTab = 'home'; setTimeout(() => window.dispatchEvent(new CustomEvent('set-create-mode', { detail: 'deal' })), 50); };
    const goCreateOff = () => { activeTab = 'home'; setTimeout(() => window.dispatchEvent(new CustomEvent('set-create-mode', { detail: 'offer' })), 50); };
    const goPricing = () => { activeTab = 'pricing'; };
    const onNavigate = (e) => {
      const route = e?.detail?.route;
      if (route === 'pricing') activeTab = 'pricing';
      else if (route === 'home') activeTab = 'home';
      else if (route === 'create') activeTab = 'home';
      else if (route === 'map') { window.location.search = workspace.partner_id ? `?map=1&company=${workspace.partner_id}` : '?map=1'; }
      else if (route === 'offers') activeTab = 'map';
      else if (route === 'library') activeTab = 'home';
    };
    window.addEventListener('go-create', goCreate);
    window.addEventListener('go-library', goLibrary);
    window.addEventListener('go-create-audience', goCreateAud);
    window.addEventListener('go-create-deal', goCreateDeal);
    window.addEventListener('go-create-offer', goCreateOff);
    window.addEventListener('go-pricing', goPricing);
    window.addEventListener('navigate', onNavigate);
    return () => {
      window.removeEventListener('go-create', goCreate);
      window.removeEventListener('go-library', goLibrary);
      window.removeEventListener('go-create-audience', goCreateAud);
      window.removeEventListener('go-create-deal', goCreateDeal);
      window.removeEventListener('go-create-offer', goCreateOff);
      window.removeEventListener('go-pricing', goPricing);
      window.removeEventListener('navigate', onNavigate);
    };
  });

  let profileMenuOpen = $state(false);

  $effect(() => {
    if (!profileMenuOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest('.profile-wrap')) profileMenuOpen = false;
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  });

  // Miles 2026-11-05: Home IS Create (blue): start a business or imagine. Spark and the rest move into Elvie.
  const tabs = [
    { id: 'home',    label: 'Create', accent: 'blue' }
  ];
  // The builder has no library (Miles 2026-11-05): the person's own items are in Wovyn → IP library, the company's in
  // HQ → IP → Company library. Everything stays stored with the company.

  let initial = $derived((user?.email || 'M').slice(0, 1).toUpperCase());
  let dailyUsed = $derived(workspace.quota?.daily_actions_used ?? 0);
  let dailyTotal = $derived(workspace.quota?.daily_actions_total ?? 0);
  let tierLabel = $derived((workspace.quota?.tier || 'free').replace(/^\w/, c => c.toUpperCase()));
</script>

<div class="shell">
  {#if !embed}
  <header class="topbar">
    <button class="brand" onclick={() => activeTab = 'home'} title="Home">
      <img class="brand-logo" src="https://res.cloudinary.com/dj0spw2w8/image/upload/v1767931182/BLV_Secondary_Logo_Profile_Picture_ou6q5d.png" alt="BLV" />
    </button>

    {#if mapOnly}
    <div class="map-title">Journey map{#if companyName} · {companyName}{/if}</div>
    <a class="map-back" href="/">Builder</a>
    {:else}
    <nav class="tabs">
      {#each tabs as t}
        <button
          class="tab"
          class:active={activeTab === t.id}
          class:blue={t.accent === 'blue'}
          onclick={() => activeTab = t.id}
        >{t.label}</button>
      {/each}
    </nav>
    {/if}

    {#if user && workspace.id}
      <VentureSwitcher />
    {/if}

    <div class="spacer"></div>


    <button class="pricing-btn" onclick={() => activeTab = 'pricing'}>
      <svg width="13" height="13" viewBox="0 0 14 14"><path d="M7.5 1.5 L12.5 1.5 L12.5 6.5 L6.5 12.5 L1.5 7.5 Z" fill="none" stroke="#FFFFFF" stroke-width="1.4" stroke-linejoin="round"/><circle cx="9.5" cy="4.5" r="0.9" fill="#FFFFFF"/></svg>
      Credits
    </button>

    {#if user}
      <div class="profile-wrap">
        <button
          class="profile-btn"
          onclick={(e) => { e.stopPropagation(); profileMenuOpen = !profileMenuOpen; }}
          type="button"
          aria-label="Profile"
        >
          {#if user.user_metadata?.avatar_url}
            <img src={user.user_metadata.avatar_url} alt="" class="profile-avatar-img" />
          {:else}
            <span class="profile-avatar-initial">{(user.email || '?').charAt(0).toUpperCase()}</span>
          {/if}
        </button>
        {#if profileMenuOpen}
          <div class="profile-menu">
            <div class="profile-email">{user.email}</div>
            <div class="profile-divider"></div>
            <button
              class="profile-menu-item"
              type="button"
              onclick={async (e) => {
                e.stopPropagation();
                try {
                  const { error } = await supabase.auth.signOut();
                  if (error) { console.error('[signout] error:', error); alert('Sign out failed: ' + error.message); return; }
                  profileMenuOpen = false;
                  window.location.href = '/';
                } catch (err) {
                  console.error('[signout] threw:', err);
                  alert('Sign out failed. Check console.');
                }
              }}
            >
              Sign out
            </button>
          </div>
        {/if}
      </div>
    {/if}
  </header>
  {/if}

  {#if !embed}
  <div class="counters-bar">
    {#if user}<CreditsPill />{/if}
  </div>
  {/if}

  <main>
    {#if !workspace.loaded && user}
      <div class="loading">Loading your model…</div>
    {:else if mapOnly}
      <MapTab />
    {:else if activeTab === 'home' || activeTab === 'create' || activeTab === 'map' || activeTab === 'offers'}
      <CreatePanel />
    {:else if activeTab === 'pricing'}
      <PricingView />
    {/if}
  </main>

  <Toast />
  <ElvieButton />
  <ElviePanel />
</div>

<style>
  .shell { min-height: 100vh; background: var(--bg); }
  .map-title { font-size: 15px; font-weight: 600; color: var(--text); margin-left: 12px; }
  .map-back { margin-left: 14px; color: #7ab8ff; font-size: 13px; text-decoration: none; }
  .topbar {
    display: flex;
    align-items: center;
    gap: 22px;
    padding: 14px 24px;
    border-bottom: 1px solid var(--border);
    background: rgba(8,8,10,0.92);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    position: sticky;
    top: 0;
    z-index: 50;
  }
  .brand {
    display: flex;
    align-items: center;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  }
  .brand-logo {
    height: 36px;
    width: auto;
    display: block;
    filter: drop-shadow(0 2px 8px rgba(212,175,55,0.2));
  }

  .tabs {
    display: flex;
    gap: 2px;
    margin-left: 8px;
  }
  .tab {
    background: transparent;
    border: 1px solid transparent;
    color: var(--text-muted);
    padding: 6px 14px;
    border-radius: 8px;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.1px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  }
  .tab:hover { color: var(--text); }
  .tab.active {
    background: rgba(255,255,255,0.06);
    color: var(--text);
    font-weight: 600;
  }
  .tab.blue {
    background: rgba(122,184,255,0.06);
    border-color: var(--blue-line);
    color: var(--blue);
    font-weight: 600;
  }
  .tab.blue:hover { background: rgba(122,184,255,0.1); }
  .tab.blue.active { background: rgba(122,184,255,0.14); }

  .spacer { flex: 1; }

  .tech-mode-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    color: #9aa0a6;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    cursor: not-allowed;
    user-select: none;
  }
  .cs-tag {
    background: rgba(212,175,55,0.15);
    color: #d4af37;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    letter-spacing: 0.6px;
  }

  .workspace-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px 6px 10px;
    background: rgba(255,255,255,0.04);
    border: 1px solid var(--border);
    border-radius: var(--r-pill);
    font-size: 12px;
    color: var(--text);
    font-family: var(--font-sans);
  }
  .chip-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--green);
    box-shadow: 0 0 8px rgba(124,217,146,0.6);
  }
  .chip-name { color: var(--text); }

  .pricing-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 7px 14px;
    border-radius: 10px;
    background: #0a0a0c;
    border: 1px solid var(--gold);
    color: #FFFFFF;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.2px;
    cursor: pointer;
    box-shadow: 0 6px 18px rgba(212,175,55,0.18);
    animation: blvPricingGlow 3.4s ease-in-out infinite;
  }
  .pricing-btn:hover { transform: translateY(-1px); }

  .meter {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text-dim);
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.3px;
  }
  .meter-tier { color: var(--gold); font-weight: 600; }
  .meter-sep { opacity: 0.5; }
  .meter-count { color: var(--text-muted); }

  .avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: rgba(212,175,55,0.18);
    color: var(--gold);
    border: 1px solid var(--gold-line);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .avatar:hover { background: rgba(212,175,55,0.28); }

  .loading {
    text-align: center;
    color: var(--text-faint);
    padding: 96px 0;
    font-family: var(--font-sans);
    font-size: 14px;
  }

  .profile-wrap { position: relative; }
  .profile-btn {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(212,175,55,0.18), rgba(168,134,26,0.18));
    border: 1px solid rgba(212,175,55,0.35);
    color: #f0efe9;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-weight: 600; font-size: 14px;
    padding: 0;
    overflow: hidden;
    margin: 0;
    box-shadow: none;
    transition: border-color 0.15s ease;
  }
  .profile-btn:hover {
    border-color: rgba(212,175,55,0.6);
    transform: none;
    background: linear-gradient(135deg, rgba(212,175,55,0.18), rgba(168,134,26,0.18));
  }
  .profile-avatar-img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .profile-avatar-initial { line-height: 1; }
  .profile-menu {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 200px;
    background: rgba(20,18,22,0.96);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.5);
    padding: 6px;
    z-index: 200;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  .profile-email {
    padding: 8px 12px;
    font-size: 12px;
    color: #9aa0a6;
    font-family: 'JetBrains Mono', monospace;
    word-break: break-all;
  }
  .profile-divider {
    height: 1px;
    background: rgba(255,255,255,0.08);
    margin: 4px 0;
  }
  .profile-menu-item {
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    color: #d8d8dc;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    margin: 0;
    box-shadow: none;
    transition: background 0.12s ease;
  }
  .profile-menu-item:hover {
    background: rgba(255,255,255,0.06);
    color: #fff;
    transform: none;
  }

  .tech-mode-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px 6px 14px;
    border-radius: 999px;
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    color: #9aa0a6;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    margin: 0;
    box-shadow: none;
  }
  .tech-mode-pill:hover {
    color: #d8d8dc;
    border-color: rgba(255,255,255,0.2);
    transform: none;
  }
  .tech-mode-pill.active {
    background: rgba(212,175,55,0.15);
    border-color: rgba(212,175,55,0.4);
    color: #d4af37;
  }
  .cs-tag {
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    background: rgba(255,255,255,0.06);
    color: #6a6a6e;
    padding: 2px 6px;
    border-radius: 999px;
  }
  .tech-mode-pill.active .cs-tag {
    background: rgba(212,175,55,0.3);
    color: #0a0a0c;
  }

  .counters-bar {
    display: flex;
    justify-content: flex-end;
    padding: 8px 28px 0;
    background: rgba(8,8,10,0.6);
    border-bottom: 1px solid rgba(255,255,255,0.04);
    position: sticky;
    top: 60px;
    z-index: 49;
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
  }
</style>
